import React from 'react';
import { Layout, Section, Stack } from '@fiverr-private/layout_components';
import { Typography } from '@fiverr-private/typography';
import { Button } from '@fiverr-private/button';
import { HeroCtaArrow } from '../../icons';
import { scrollToSection } from '../../../lib/scroll';
import { SECTION_IDS } from '../../../consts';
import { appTranslate } from '../../i18n';
import { useHeroAnimation } from '../../../lib/hooks';
import { useBI, useImpression, SECTION_NAMES } from '../../bi';
// eslint-disable-next-line no-duplicate-imports
import type { BIEvents } from '../../bi';
import { Header } from '../Header';
import { HEADLINE_STYLE } from './constants';
import { heroWord, heroFade, ctaButton, ctaArrowOnHover } from './styles.ve.css';

const makeCtaClick = (bi: BIEvents, ctaType: string, sectionId: string) => () => {
    bi.reportCtaClick({ componentName: ctaType });
    scrollToSection(sectionId, { instant: true });
};

export const HeroSection = () => {
    const { wordRefs } = useHeroAnimation();
    const headlineWords = appTranslate('hero.headline').split(' ');
    const bi = useBI();
    const impressionRef = useImpression<HTMLDivElement>(() =>
        bi.reportSectionImpression({ sectionName: SECTION_NAMES.hero })
    );
    const emphasisWords = appTranslate('hero.headline_emphasis').split(' ');

    return (
        <Section fullWidth backgroundColor="white">
            <Stack
                ref={impressionRef}
                direction="column"
                position="relative"
                height="100vh"
                overflow="hidden"
                paddingX={{ default: '5', md: '14' }}
                gap="0"
            >
                <Header />

                <Stack
                    direction="column"
                    justifyContent="center"
                    flexGrow={1}
                    paddingTop={{ default: '14', md: '0' }}
                    gap="6"
                >
                    <Typography as="h1" fontWeight="normal" color="black" maxWidth="1500px" style={HEADLINE_STYLE}>
                        {headlineWords.map((word, i) => (
                            <React.Fragment key={i}>
                                {i > 0 && ' '}
                                <Layout as="span" display="inlineBlock">
                                    {word}
                                </Layout>
                            </React.Fragment>
                        ))}
                        <Layout as="span" display="block" style={{ fontWeight: 'bold' }}>
                            {emphasisWords.map((word, i) => (
                                <React.Fragment key={`e${i}`}>
                                    {i > 0 && ' '}
                                    <Layout
                                        as="span"
                                        display="inlineBlock"
                                        className={heroWord}
                                        ref={(el: HTMLElement | null) => {
                                            wordRefs.current[i] = el;
                                        }}
                                    >
                                        {word}
                                    </Layout>
                                </React.Fragment>
                            ))}
                        </Layout>
                    </Typography>
                </Stack>

                <Stack
                    direction={{ default: 'column', md: 'row' }}
                    gap={{ default: '5', md: '8' }}
                    width={{ default: '100%', md: 'auto' }}
                    alignItems={{ default: 'stretch', md: 'center' }}
                    alignSelf="flexEnd"
                    marginBottom={{ default: '7', md: '14' }}
                >
                    <Button
                        size="lg"
                        fullWidth={{ default: true, md: false }}
                        className={`${heroFade} ${ctaButton}`}
                        style={{ borderRadius: 9999 }}
                        onClick={makeCtaClick(bi, 'view_roles', SECTION_IDS.openPositions)}
                    >
                        <Stack as="span" gap="2" alignItems="center" justifyContent="center" paddingLeft="4">
                            {appTranslate('hero.view_open_roles')}
                            <Layout
                                as="span"
                                className={ctaArrowOnHover}
                                display={{ default: 'none', md: 'inlineFlex' }}
                                transform="rotate(-30deg)"
                            >
                                <HeroCtaArrow />
                            </Layout>
                        </Stack>
                    </Button>
                    <Button
                        size="lg"
                        variant="outline"
                        fullWidth={{ default: true, md: false }}
                        className={heroFade}
                        style={{ borderRadius: 9999 }}
                        onClick={makeCtaClick(bi, 'apply_unusual', SECTION_IDS.apply)}
                    >
                        {appTranslate('hero.apply_unusual_way')}
                    </Button>
                </Stack>
            </Stack>
        </Section>
    );
};
